"use client";

import React from "react";

import Image from "next/image";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";

import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import { CTAWrapper } from "@/components/global/utils/CTAWrapper";
import { useCTAWrapper } from "@/hooks/useCTAWrapper";
import { CTACornerButton } from "@/shadcn/ui/corner-button";

import { PageCustomersSchema } from "@/sanity/pages/PageCustomersTemplate";

// the same 959×202 pixel grid the rest of the new theme sits under
import dots from "@/public/vsl-short/dots.svg";
import dotsM from "@/public/vsl-short/dots-m.svg";
import ScrollingBanner from "../global/ui/ScrollingBanner";

const fadeInUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" as const },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
});

export default function HeroSection({ data, className }: { data: PageCustomersSchema; className?: string }) {
  // the text link below the CTA carries its own label, so it needs the resolver
  // the corner button gets for free
  const { title: linkTitle } = useCTAWrapper(data?.heroLink);

  return (
    <section
      className={cn(
        "relative flex flex-col items-center px-[2rem] pt-[8rem] pb-[18rem] sm:px-[3rem] md:px-[5rem] lg:pt-[10.75rem] lg:pb-[24rem]",
        className,
      )}>
      {/* horizontal divider under the header */}
      <DecorationLine
        className="top-0"
        orientation="horizontal"
        style="dark"
      />

      {/* vertical side lines flanking the content (near the edges on mobile, at the Figma inset on desktop) */}
      <DecorationLine
        className="top-0 left-[1rem] lg:left-[8.85%]"
        orientation="vertical"
        style="dark"
      />
      <DecorationLine
        className="top-0 right-[1rem] lg:right-[8.85%]"
        orientation="vertical"
        style="dark"
      />

      <Image
        src={dots}
        alt=""
        aria-hidden
        className="pointer-events-none absolute top-0 left-1/2 z-0 hidden !w-[119.875rem] -translate-x-1/2 lg:block"
      />
      <Image
        src={dotsM}
        alt=""
        aria-hidden
        className="pointer-events-none absolute top-0 left-1/2 z-0 !w-full -translate-x-1/2 lg:hidden"
      />

      {/* eyebrow chip */}
      {data?.heroChip?.text && (
        <motion.div {...fadeInUp()}>
          <Chip
            className="relative z-[1] mb-[2.5rem] [&_p]:font-geistmono [&_p]:text-[#202223] [&_p]:uppercase"
            image={data?.heroChip?.icon}
            text={data?.heroChip?.text}
          />
        </motion.div>
      )}

      {/* headline — the "new" theme already renders h1 as Funnel, so only the
          serif italic run and the desktop size need spelling out here */}
      {data?.heroTitle && (
        <motion.div
          {...fadeInUp(0.1)}
          className="pixel-highlight richtext richtext-center relative z-[1] w-[min(120rem,100%)] prose-em:!px-0 prose-em:font-ivy lg:prose-em:!px-[.7rem] prose-headings:mb-0 prose-headings:text-[4.25rem] prose-headings:leading-none lg:prose-headings:text-[8rem]">
          <PortableText
            value={data.heroTitle}
            components={richTextComponent}
          />
        </motion.div>
      )}

      {/* subtext */}
      {data?.heroText && (
        <motion.div
          {...fadeInUp(0.2)}
          className="richtext richtext-center relative z-[1] mt-[2rem] w-[min(63.375rem,100%)] prose-p:mb-0 prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:text-black/60">
          <PortableText
            value={data.heroText}
            components={richTextComponent}
          />
        </motion.div>
      )}

      {data?.heroCTA && (
        <motion.div
          {...fadeInUp(0.3)}
          className="relative z-[1]">
          <CTACornerButton
            className="mt-[3.5rem]"
            data={data.heroCTA}
          />
        </motion.div>
      )}

      {data?.heroLink && linkTitle && (
        <motion.div {...fadeInUp(0.4)}>
        <CTAWrapper
          cta={data.heroLink}
          className="relative z-[1] mt-[2rem] cursor-pointer">
          <p className="m-0 text-center font-geist text-[2.25rem] leading-[1.5] text-black/60 underline decoration-from-font underline-offset-[.25rem] transition-colors hover:text-black">
            {linkTitle}
          </p>
        </CTAWrapper>
        </motion.div>
      )}
      {data?.heroBanner?.length > 0 && (
        <div className="absolute inset-x-0 bottom-0">
          <ScrollingBanner text={data.heroBanner} />
        </div>
      )}
    </section>
  );
}
